/* ============================================================
   CafeVista — Single source of structured content.
   All copy here is derived from the real CafeVista website /
   codebase (services, projects, team, contact). Nothing invented.
   Copy has been sharpened for clarity and conversion only.
   Pure data (no asset imports) so it stays portable + serializable.
   ============================================================ */

export const brand = {
  name: "CafeVista",
  stylized: "CafeVist@",
  legalName: "Cafevista Pvt Ltd",
  parent: "Cafevista Pvt Ltd",
  domain: "cafevistaai.com",
  // Pre-rebrand name. Kept for schema.org alternateName so searches for the
  // old brand still resolve here - see src/config/seo.js.
  legacyName: "CafeVistaAI",
  promise: "Business challenges in. Working solutions out.",
  tagline: "We engineer business solutions and integrate them into how you actually operate.",
  // Rotating hero lines — business outcome first, technology second.
  heroHeadlines: [
    "You bring the business challenge. We engineer the solution.",
    "Connect any ERP, any database — get one system that makes sense of it.",
    "Solutions that don't sit beside your business. They run inside it.",
  ],
};

/* ---------- The core narrative (drives the homepage scroll story) ---------- */
export const corePromise = {
  eyebrow: "Business Solutions",
  title: "A business challenge arrives. A working solution leaves.",
  lead:
    "CafeVista is a business solutions company. We start with the problem you actually have — not the technology we'd like to sell — then engineer, integrate and run the system that fixes it.",
  steps: [
    "You bring the challenge",
    "We understand it",
    "We engineer the solution",
    "We integrate it",
    "The business works better",
  ],
};

/* ---------- Group architecture: Cafevista Pvt Ltd & its ventures ---------- */
export const company = {
  parent: "Cafevista Pvt Ltd",
  since: "2018",
  tagline: "A business solutions group — engineering systems that businesses run on since 2018",
};

export const ventures = [
  {
    id: "vistaai",
    name: "VistaAI",
    kicker: "The AI Studio",
    accent: "cyan",
    icon: "brain",
    short: "AI & Automation Solutions",
    positioning:
      "The software studio behind the products: business systems, integrations and custom applications, built and supported in-house.",
    body:
      "We design, build and run business software — ERP integrations, data platforms, internal tools, portals, commerce, mobile and embedded systems. 150+ projects delivered for 100+ clients since 2018.",
    stats: [
      { v: "150+", l: "Projects" },
      { v: "100+", l: "Clients" },
      { v: "5+", l: "Years" },
      { v: "24/7", l: "Support" },
    ],
    cta: { label: "Explore Solutions", to: "/service" },
    to: "/service",
  },
  {
    id: "collabiz",
    name: "Collabiz",
    kicker: "The ERP Platform",
    accent: "violet",
    icon: "gears",
    logo: "collabiz",
    badge: "NOW LIVE",
    short: "B2B Operating Network — now live",
    positioning:
      "The B2B operating system for manufacturers — orders, production, inventory and an AI co-pilot in one real-time core.",
    body:
      "CollaBiz unifies orders, production costing, inventory ledgers, payables and a B2B trading network into one real-time, AI-driven ERP. Built for manufacturers ready to retire the spreadsheets.",
    features: ["Orders & Production", "Inventory Ledger", "B2B Network", "AI Co-pilot"],
    launch: "Live now",
    // Bundled cinematic landing at Frontend/public/collabiz.html (assets in
    // public/collabiz/). A real file path, so SPA dev servers serve it instead
    // of rewriting a directory URL to index.html.
    external: "/collabiz.html",
    cta: { label: "Explore CollaBiz", href: "/collabiz.html" },
  },
];

/* ---------- Clients & partners ----------
   MB Industries (Hira Plastics) is a CafeVista client and our industrial
   partner - not a Cafevista venture. Keep that distinction in copy. */
export const industrialPartner = {
  id: "mbindustries",
  name: "MB Industries",
  brand: "Hira Plastics",
  fullName: "Mian Brothers Industries",
  kicker: "Client & industrial partner",
  accent: "green",
  icon: "chip",
  short: "Plastic film manufacturing - Multan, Pakistan",
  positioning:
    "A manufacturing client we build for, and the industrial partner behind our factory-floor experience.",
  body:
    "Mian Brothers Industries (founded 2014) manufactures plastic film for agriculture, construction and packaging under its Hira Plastics brand, exporting to 5+ countries at 7,000+ tons a year. We build and run their systems - and the work is why our software understands production floors, batch costing and stock ledgers rather than just dashboards.",
  stats: [
    { v: "7k+", l: "Tons / year" },
    { v: "5+", l: "Export markets" },
    { v: "2014", l: "Founded" },
    { v: "2022", l: "Working together since" },
  ],
  products: [
    "Agricultural Film",
    "Tunnel Farming Rolls",
    "Construction Sheets",
    "Packaging Solutions",
    "Mulching Sheets",
  ],
  external: "https://hiraaplastics.com",
  cta: { label: "Visit hiraaplastics.com", href: "https://hiraaplastics.com" },
};

/* ---------- Our own products ----------
   Everything here is a real, shipped CafeVista product. `shots` name files in
   src/assets/work/. Do not add a product to this list before it exists. */
export const products = [
  {
    id: "collabiz",
    name: "CollaBiz",
    kicker: "B2B / ERP platform",
    status: "live",
    accent: "violet",
    icon: "gears",
    logo: "collabiz",
    summary:
      "One real-time core for orders, production costing, inventory ledgers and payables, plus a B2B trading network and an AI co-pilot. Built for manufacturers who have outgrown spreadsheets but are not ready for enterprise licensing.",
    features: [
      "Orders, production & dispatch",
      "Inventory ledger & procurement",
      "Customer ledger & payables",
      "B2B network, RFQs & suppliers",
    ],
    shots: ["collabiz-dashboard.webp"],
    href: "/collabiz.html",
    ctaLabel: "Explore CollaBiz",
  },
  {
    id: "datavista",
    name: "DataVista",
    kicker: "Analytics over your ERP",
    status: "live",
    accent: "cyan",
    icon: "chart",
    summary:
      "Connects to SAP and other business systems and puts one governed layer over them: executive summary, sales, customers, operations, procurement and inventory reporting, with forecasting and a semantic model behind every number.",
    features: [
      "Live SAP-connected reporting",
      "Governed KPI catalog & semantic layer",
      "Dashboard builder with version history",
      "Forecasting, alerts & automations",
    ],
    shots: ["datavista-sales.webp", "datavista-inventory.webp", "datavista-builder.webp"],
    href: "/contactus?project=DataVista",
    ctaLabel: "Request a walkthrough",
  },
  {
    id: "aianalyst",
    name: "AI Analyst",
    kicker: "Ask your ERP in plain English",
    status: "live",
    accent: "electric",
    icon: "chat",
    summary:
      "Ask questions like “what are the top 3 customers” and get an answer off live ERP data — sales orders, purchase orders, invoices, materials, deliveries and journal entries — with follow-up suggestions and one-click export to Excel.",
    features: [
      "Natural-language queries on live data",
      "Sales, purchasing, inventory & finance",
      "Export to Excel, drill into the detail",
      "Suggested follow-up questions",
    ],
    shots: ["ai-analyst-sap.webp"],
    href: "/contactus?project=AI%20Analyst",
    ctaLabel: "See it on your data",
  },
  {
    id: "chatvista",
    name: "ChatVista",
    kicker: "Chatbot as a service",
    status: "live",
    accent: "coffee",
    icon: "bot",
    summary:
      "Point it at your knowledge base, brand it, and embed it anywhere — WordPress, Shopify or raw HTML. Answers from your own documents rather than guessing, and captures the lead or books the appointment while it is at it.",
    features: [
      "RAG-powered answers from your documents",
      "Lead capture & appointment booking",
      "Support tickets & handover",
      "WordPress / Shopify / HTML embed",
    ],
    shots: ["chatvista-landing.webp"],
    href: "/contactus?project=ChatVista",
    ctaLabel: "Talk about a chatbot",
  },
  {
    id: "vistabridge",
    name: "VistaBridge",
    kicker: "Your AI assistant, on your data",
    status: "live",
    accent: "violet",
    icon: "signal",
    summary:
      "Point Claude, ChatGPT or Copilot at your own ERP, database or spreadsheets and let your team ask questions in plain language — stock on hand, who is overdue, what shipped last week. We build the MCP server that sits between them, so the assistant reads your real data instead of guessing.",
    features: [
      "Works with Claude, ChatGPT, Copilot and IDE agents",
      "Connects SAP, Oracle, SQL, sheets or your own API",
      "Read-only by default, scoped to what you name",
      "Writes only behind an explicit human approval",
    ],
    shots: [],
    diagram: "bridge",
    href: "/contactus?project=VistaBridge",
    ctaLabel: "Connect one system",
  },
];

/* ---------- AI agent connectors ----------
   The AI vendors below are named nominatively: CafeVista builds MCP servers
   that these clients connect to. We are not a partner or reseller of any of
   them, so they are set as plain wordmarks, not their logotypes. */
export const agentClients = [
  { id: "claude", mark: "Claude", note: "Anthropic · native MCP" },
  { id: "chatgpt", mark: "ChatGPT", note: "OpenAI · connectors" },
  { id: "copilot", mark: "Copilot", note: "Microsoft 365" },
  { id: "cursor", mark: "Cursor & IDEs", note: "Developer agents" },
];

/* ---------- Digital products ----------
   Not shipped yet. Marked "planned" on purpose: nothing here should read as
   available to buy until a storefront actually exists. */
export const digitalProducts = {
  status: "planned",
  heading: "Digital products",
  lead:
    "We are packaging the templates and design systems we build for clients into products you can buy off the shelf. Not open yet — tell us what you would want and we will let you know when it is.",
  items: [
    {
      id: "shopify-themes",
      name: "Shopify themes",
      icon: "cart",
      body: "Conversion-focused storefront themes built from the commerce work we already ship.",
    },
    {
      id: "design-systems",
      name: "Design systems & UI kits",
      icon: "spark",
      body: "The component libraries and brand systems behind our product work, packaged to reuse.",
    },
    {
      id: "dashboard-templates",
      name: "Dashboard templates",
      icon: "chart",
      body: "Reporting layouts and KPI packs that plug into an existing data warehouse.",
    },
  ],
  cta: { label: "Tell us what you need", to: "/contactus?project=Digital%20products" },
};

export const contact = {
  email: "cafevistaai@gmail.com",
  supportEmail: "support@cafevistaai.com",
  phone: "+921375948528",
  phoneDisplay: "+92 137 594 8528",
  address: "1st Floor, Al Harmain Plaza, Near Sultana Foundation, Lethrar Road",
  region: "Islamabad, Pakistan",
  hours: "Open 24/7",
  socials: {
    instagram: "https://instagram.com/cafevistaai",
    facebook: "https://www.facebook.com/cafevistAi",
    youtube: "https://www.youtube.com/@CAFEVISTAAI",
    linkedin: "https://www.linkedin.com/company/cafevistaai/",
  },
};

export const navLinks = [
  { label: "Home", to: "/home" },
  { label: "About", to: "/about" },
  { label: "Solutions", to: "/service" },
  { label: "Contact", to: "/contactus" },
];

/* ---------- What CafeVista does ---------- */
export const whatWeDo = {
  heading: "We solve business problems. Software is how.",
  body:
    "CafeVista is a business solutions company. Operations that leak time, data scattered across systems nobody can read, decisions made on last month's numbers — those are the problems we take on. We study how your business actually runs, engineer the system that fixes the bottleneck, integrate it with what you already own, and stay on it after go-live.",
  capabilities: [
    "Operations & Workflow",
    "Data & Decision Systems",
    "ERP Integration",
    "Automation",
    "Customer & Sales Systems",
    "Custom Business Software",
    "Industrial & IoT",
    "Ongoing Support",
  ],
};

/* ---------- Business challenges we take on ---------- */
export const problems = [
  {
    icon: "gears",
    title: "The work runs on people, not systems",
    pain: "Orders, approvals and handoffs live in inboxes and spreadsheets. Every step needs someone to chase it, and the business can't grow faster than the chasing.",
    solution: "We map the real workflow, then engineer it into a system that moves the work itself — with the exceptions escalated, not the whole process.",
  },
  {
    icon: "chart",
    title: "The data exists, but nobody can act on it",
    pain: "It sits in the ERP, the accounting software, the warehouse sheet and three databases. Getting one straight answer takes two days and a phone call.",
    solution: "We connect the sources into one layer and put a live view on top — dashboards, alerts, and a chatbot your team can just ask.",
  },
  {
    icon: "clock",
    title: "Decisions are made on last month's numbers",
    pain: "By the time the report is compiled, the window to act on it has closed. Planning becomes guesswork dressed up as analysis.",
    solution: "Real-time reporting off live operational data, with forecasting where the history supports it. You see the problem while you can still fix it.",
  },
  {
    icon: "chip",
    title: "The systems you own don't talk to each other",
    pain: "SAP here, a custom app there, a supplier portal that exports CSVs. Integration is manual, fragile, and quietly expensive.",
    solution: "We build the integration layer — connectors, pipelines and APIs — so your existing systems behave like one. No rip-and-replace.",
  },
];

/* ---------- Solution pillars (the grounded Solutions section) ----------
   Serious, business-first. Enterprise systems are named where real:
   CafeVista's relationship is integration & extension.               */
export const solutionPillars = [
  {
    id: "datasphere",
    name: "Data Sphere",
    kicker: "Connect anything. See everything.",
    icon: "chart",
    accent: "cyan",
    description:
      "Connect any ERP, database or data source — SAP, Oracle, Dynamics, Salesforce, Odoo, NetSuite, or a plain SQL server — into one layer, and get a top-level system above it: live dashboards, real-time updates and a chatbot your team can ask in plain language. Bring your own ERP or use ours.",
    outcomes: [
      "One live view across disconnected systems",
      "Ask your data a question, get an answer",
      "Alerts when a number moves, not a month later",
    ],
  },
  {
    id: "erp",
    name: "ERP & Enterprise Systems",
    kicker: "Extend what you own. Or replace what you've outgrown.",
    icon: "gears",
    accent: "violet",
    description:
      "We work around and on top of enterprise systems — building the connectors, custom modules, portals and reporting the standard install doesn't give you. Where a business has outgrown spreadsheets but isn't ready for enterprise licensing, we deploy CollaBiz, our own B2B/ERP platform.",
    outcomes: [
      "Custom apps and portals on your existing ERP",
      "Migration on, off and between systems",
      "CollaBiz as a full ERP alternative",
    ],
  },
  {
    id: "integration",
    name: "Integration & Automation",
    kicker: "Make the systems behave like one.",
    icon: "signal",
    accent: "electric",
    description:
      "The connective tissue: APIs, data pipelines, sync jobs, and process automation across the tools you already run. We remove the manual re-keying between systems and automate the workflow that sits between them.",
    outcomes: [
      "System-to-system integration, no re-keying",
      "Automated approvals, documents and handoffs",
      "Scheduled and event-driven pipelines",
    ],
  },
  {
    id: "agents",
    name: "AI Agent Connect",
    kicker: "Let the assistant your team already uses read your business.",
    icon: "signal",
    accent: "violet",
    description:
      "Your team is already using Claude or ChatGPT. We connect it to the systems that hold your actual numbers — through an MCP server we build and you control — so the answers come from your ERP and your database rather than a guess. Read-only to start, scoped to the tables you name, and a working connection to one source inside 48 hours.",
    outcomes: [
      "One source connected and answering in 48 hours",
      "Read-only by default, writes need approval",
      "No copying your data into someone else's tool",
    ],
  },
  {
    id: "custom",
    name: "Custom Business Software",
    kicker: "When nothing off the shelf fits.",
    icon: "phone",
    accent: "coffee",
    description:
      "Purpose-built systems for the part of your business that is genuinely yours — operations platforms, customer portals, field and mobile apps, e-commerce, and the industrial and IoT layer where software meets the factory floor.",
    outcomes: [
      "Operations platforms and internal tools",
      "Customer portals, mobile and commerce",
      "Embedded, IoT and industrial systems",
    ],
  },
];

/* ---------- Enterprise systems we connect to ----------
   NOTE: relationship is integration & extension — NOT an implementation
   partnership. Copy must stay "we connect to / build on", never "partner of".
   `mark` is a plain wordmark; drop an official SVG into src/assets/logos/
   and add `logo: "<file>.svg"` here to swap in real artwork.        */
export const enterpriseSystems = [
  { id: "sap", mark: "SAP", note: "ECC · S/4HANA · Datasphere" },
  { id: "oracle", mark: "ORACLE", note: "E-Business Suite · Fusion" },
  { id: "dynamics", mark: "Microsoft Dynamics", note: "Business Central · Power BI" },
  { id: "salesforce", mark: "Salesforce", note: "Sales & Service Cloud" },
  { id: "netsuite", mark: "NetSuite", note: "Mid-market ERP" },
  { id: "odoo", mark: "Odoo", note: "Open-source ERP" },
  { id: "sql", mark: "SQL / REST", note: "Any database or API" },
  { id: "collabiz", mark: "CollaBiz", note: "Our own B2B ERP", own: true },
];

/* ---------- Business functions a solution integrates into ---------- */
export const integrationAreas = [
  { id: "ops", label: "Operations", icon: "gears", detail: "Orders, production, scheduling, dispatch" },
  { id: "finance", label: "Finance", icon: "chart", detail: "Costing, payables, receivables, margin" },
  { id: "customer", label: "Customer Flow", icon: "chat", detail: "Enquiry, quote, order, support" },
  { id: "supply", label: "Supply & Inventory", icon: "cart", detail: "Stock ledger, procurement, suppliers" },
  { id: "people", label: "People & Workflow", icon: "team", detail: "Approvals, handoffs, accountability" },
  { id: "intel", label: "Intelligence", icon: "brain", detail: "Reporting, forecasting, alerts" },
];

/* ---------- The six scenes of the homepage scroll story ----------
   Each entry is one scene of the continuous world. Copy here is what the
   user reads while that scene is on screen — keep it short.        */
export const storyScenes = [
  {
    id: "challenge",
    step: "01",
    label: "The challenge arrives",
    title: "It starts with a problem, not a product.",
    body: "A brief lands. Margins slipping, orders slipping, a system nobody trusts. Whatever the shape of it, this is where every engagement actually begins.",
  },
  {
    id: "discovery",
    step: "02",
    label: "Discovery",
    title: "We understand it before we solve it.",
    body: "We pull in how the business really runs — the systems, the spreadsheets, the workarounds people invented. Scattered inputs get sorted, connected and clarified until the real bottleneck is visible.",
  },
  {
    id: "engineering",
    step: "03",
    label: "Engineering",
    title: "The right solution gets engineered.",
    body: "Architecture, data model, integration points, interface. Components are built, aligned and connected — refined against the business case, not a feature list.",
  },
  {
    id: "solution",
    step: "04",
    label: "The solution",
    title: "One integrated system takes shape.",
    body: "Data, workflow, automation, integration and reporting stop being separate projects. They become a single working system with one way in and one version of the truth.",
  },
  {
    id: "integration",
    step: "05",
    label: "Integration",
    title: "It moves into the business.",
    body: "The solution connects to operations, finance, customer flow, supply and the systems you already own. Not a pilot sitting to one side — wired into how the work actually happens.",
  },
  {
    id: "vista",
    step: "06",
    label: "The Vista",
    title: "The business runs better.",
    body: "Flows straighten out. Coordination stops needing chasing. Numbers arrive while they still matter, and the capacity that was going into holding it all together goes back into growth.",
  },
];

/* ---------- How we work ---------- */
export const journey = [
  { step: "01", title: "Understand", coffee: "Discovery", body: "We map how the business runs today — systems, data, people, and where the money and time actually leak." },
  { step: "02", title: "Define", coffee: "Scope", body: "We agree the business outcome, then design the solution and integration plan to reach it. Fixed scope, clear cost." },
  { step: "03", title: "Engineer", coffee: "Build", body: "We build the system — data layer, integrations, automation and interface — in reviewable increments, not a year-long black box." },
  { step: "04", title: "Integrate", coffee: "Connect", body: "We connect it to your existing ERP, databases and tools, migrate what needs migrating, and validate against real operations." },
  { step: "05", title: "Go live", coffee: "Adopt", body: "We train the team, run parallel where it matters, and cut over. Adoption is part of delivery, not an afterthought." },
  { step: "06", title: "Improve", coffee: "Run", body: "We stay on it — monitoring, support, and the next increment as the business changes." },
];

/* ---------- Why choose CafeVista ---------- */
export const whyChoose = {
  heading: "Why businesses bring us the hard problems",
  reasons: [
    {
      icon: "gears",
      title: "We start with the business, not the stack",
      body: "Discovery is about how you make money and where it leaks. The technology decision comes after that conversation, not before it.",
    },
    {
      icon: "signal",
      title: "We work with what you already own",
      body: "SAP, Oracle, Dynamics, Salesforce, Odoo, NetSuite or a database somebody built in 2011 — we integrate and extend rather than force a rip-and-replace.",
    },
    {
      icon: "shield",
      title: "Integration is the deliverable",
      body: "A solution that isn't wired into operations, finance and customer flow is a demo. We deliver it running inside the business, with the team using it.",
    },
    {
      icon: "team",
      title: "One accountable team, transparent cost",
      body: "Engineers, data and design under one roof, one point of contact, scoped pricing with no hidden line items, and support that continues after go-live.",
    },
  ],
};

/* ---------- Stats (updated group voice) ---------- */
export const stats = [
  { value: 100, suffix: "+", label: "Clients served", icon: "users" },
  { value: 150, suffix: "+", label: "Projects delivered", icon: "check" },
  { value: 8, suffix: "", label: "Years building", icon: "clock" },
  { value: 4, suffix: "", label: "Products in production", icon: "gears" },
];

/* Extended stats for the About page (adds the industrial partnership) */
export const aboutStats = [
  { value: 100, suffix: "+", label: "Clients served", icon: "users" },
  { value: 150, suffix: "+", label: "Projects delivered", icon: "check" },
  { value: 8, suffix: "", label: "Years building", icon: "clock" },
  { value: 4, suffix: "", label: "Products in production", icon: "gears" },
];

/* ---------- About: company timeline ---------- */
export const timeline = [
  { year: "2018", title: "Founded", body: "Started as a small software consultancy solving one client problem at a time." },
  { year: "2020", title: "VistaAI Takes Shape", body: "Expanded from software delivery into data, automation and business systems." },
  { year: "2022", title: "Onto the factory floor", body: "Started building for MB Industries (Hira Plastics) — production, costing and stock, not just software." },
  { year: "2024", title: "Cafevista Pvt Ltd incorporated", body: "Registered as a company, with the software studio and product work under one roof." },
  { year: "2025", title: "Products ship", body: "CollaBiz goes live alongside DataVista and the AI Analyst — our own products, running on client data." },
];

/* ---------- About: mission / vision / values ---------- */
export const missionVisionValues = [
  {
    key: "What we do",
    icon: "gears",
    body: "We take on the operational problems a business has already tried to fix with people and spreadsheets, and we solve them with a system instead — built onto whatever software the business already runs.",
  },
  {
    key: "Where we are going",
    icon: "chart",
    body: "Enterprise-grade reporting and automation cost enterprise money. We are building products that put the same capability inside mid-sized manufacturers and distributors, at a price that makes sense for them.",
  },
  {
    key: "How we work",
    icon: "shield",
    body: "Scoped price agreed before we start, no line items that appear later. Reviewable increments rather than a year-long black box. And we stay on the system after go-live, because that is when it actually gets used.",
  },
];

/* ---------- Services: 11 domains (updated copy) ---------- */
export const serviceCategories = [
  { id: "software", name: "Software Apps", icon: "phone", accent: "cyan", value: "Custom desktop & mobile apps built on React, Flutter, and .NET — tailored to your workflow, not a template.", items: ["Desktop Applications", "Mobile Applications", "Web Applications"] },
  { id: "embedded", name: "Embedded Systems", icon: "chip", accent: "electric", value: "Microcontroller programming, IoT firmware, and real-time embedded solutions for industrial and consumer hardware.", items: ["Microcontroller Programming", "Embedded Software", "IoT Applications"] },
  { id: "ecommerce", name: "E-Commerce", icon: "cart", accent: "coffee", value: "End-to-end e-commerce — from Shopify builds to custom platforms, dropshipping setups, and conversion-optimised storefronts.", items: ["Platform Development", "Dropshipping", "Conversion Optimization", "Marketing & SEO"] },
  { id: "apps", name: "Apps & Games", icon: "play", accent: "violet", value: "iOS, Android, Unity 3D, Unreal Engine — from hybrid apps to immersive AR/VR game worlds.", items: ["iOS & Android", "Unity / Unreal", "2D · 3D · AR/VR"] },
  { id: "marketing", name: "Digital Marketing & ASO", icon: "signal", accent: "gold", value: "Data-driven SEO, App Store Optimisation, paid campaigns, and content strategy that drives real traffic.", items: ["SEO & ASO", "Paid Campaigns", "Content Strategy"] },
  { id: "ai", name: "AI & Chatbots", icon: "brain", accent: "cyan", value: "LLM-powered chatbots, ML pipelines, computer vision, NLP solutions, and custom GPT integrations for your business.", items: ["LLM Chatbots", "ML & Computer Vision", "Custom GPT Integrations"] },
  { id: "web", name: "Web Development", icon: "chart", accent: "electric", value: "Fast, scalable websites and web apps — Next.js, React, full-stack builds with APIs and CMS integration.", items: ["Next.js / React", "Full-Stack & APIs", "CMS Integration"] },
  { id: "simulators", name: "Simulators & VR/AR", icon: "spark", accent: "violet", value: "Training simulators, AR product demos, and VR experiences built on Unity and Unreal.", items: ["Training Simulators", "AR Product Demos", "VR Experiences"] },
  { id: "automation", name: "Business Automation", icon: "gears", accent: "cyan", value: "Automate repetitive workflows with custom RPA, API integrations, and intelligent process bots.", items: ["Custom RPA", "API Integrations", "Process Bots"] },
  { id: "design", name: "Design", icon: "spark", accent: "coffee", value: "Brand identity, UI/UX design, motion graphics, and product design that converts visitors into customers.", items: ["Brand Identity", "UI/UX Design", "Motion Graphics"] },
  { id: "industrial", name: "Industrial Solutions", icon: "chip", accent: "green", value: "Agricultural films, packaging materials, construction rolls, LDPE/LLDPE solutions — supplied through our industrial partner MB Industries.", items: ["Agricultural Films", "Packaging & Construction", "LDPE / LLDPE"] },
];

/* ---------- Work shown on the homepage ----------
   Every entry is a real screenshot in src/assets/work/. `outcome` is the line
   a buyer actually cares about - keep it concrete, never a slogan. */
export const work = [
  {
    id: "datavista",
    name: "DataVista",
    category: "ERP analytics",
    shot: "datavista-sales.webp",
    description:
      "Sales, inventory and finance reporting built straight onto a client's SAP system, replacing spreadsheets compiled by hand every month.",
    outcome: "148K orders and 1.12B in order value reporting live",
    tags: ["SAP", "Analytics", "Forecasting"],
    to: "/products#datavista",
  },
  {
    id: "aianalyst",
    name: "AI Analyst",
    category: "Conversational analytics",
    shot: "ai-analyst-sap.webp",
    description:
      "An assistant sitting on live ERP data. Staff ask in plain English - top customers, late orders, stock cover - and get the answer with an Excel export.",
    outcome: "Answers in seconds instead of a request to the reporting team",
    tags: ["LLM", "SAP", "Self-service"],
    to: "/products#aianalyst",
  },
  {
    id: "collabiz",
    name: "CollaBiz",
    category: "B2B / ERP platform",
    shot: "collabiz-dashboard.webp",
    description:
      "Our own operating system for manufacturers: orders, production, inventory ledgers, payables and a supplier network in one real-time core.",
    outcome: "Orders, production and dues in one place, live",
    tags: ["ERP", "Manufacturing", "B2B"],
    to: "/products#collabiz",
  },
  {
    id: "inventory",
    name: "Inventory intelligence",
    category: "Operations",
    shot: "datavista-inventory.webp",
    description:
      "Stock value broken down by material, brand and product group, off live warehouse data, so buying decisions stop being made from last month's sheet.",
    outcome: "Stock position visible by brand and product group, daily",
    tags: ["Inventory", "Procurement", "BI"],
    to: "/products#datavista",
  },
  {
    id: "chatvista",
    name: "ChatVista",
    category: "Conversational AI",
    shot: "chatvista-landing.webp",
    description:
      "Chatbot-as-a-service: connect a knowledge base, brand it, embed it on WordPress, Shopify or plain HTML, and let it capture leads and book appointments.",
    outcome: "Support answered and leads captured around the clock",
    tags: ["RAG", "Lead capture", "Embed"],
    to: "/products#chatvista",
  },
  {
    id: "builder",
    name: "Governed dashboard builder",
    category: "Data platform",
    shot: "datavista-builder.webp",
    description:
      "A drag-and-drop dashboard builder where every widget must bind to an approved KPI, with version history and immutable drafts before release.",
    outcome: "One approved definition per KPI across the business",
    tags: ["Governance", "Semantic layer", "Self-service"],
    to: "/products#datavista",
  },
];

/* ---------- Testimonials (real reviews from the live site) ---------- */
export const testimonials = [
  {
    name: "Haytham",
    role: "Manager",
    text: "I am so glad to work with them — they can do the work perfectly, and for any future chance I will not hesitate to contact them again.",
  },
  {
    name: "Abhibagel",
    role: "Entrepreneur",
    text: "Working with the team was one of the best experiences I had. The service was excellent and top-notch — always ready to work and meet our requirements, explaining everything clearly. 10/10 for quality.",
  },
  {
    name: "Soeng Jim Jim",
    role: "Artist",
    text: "Delivery was on time and I got exactly the code I wanted. Even after delivery they kept in touch until I was fully satisfied. A great experience — I'll definitely work with them again.",
  },
  {
    name: "Jay",
    role: "Consultant",
    text: "Very skilled and very helpful with the project. I definitely recommend them if you have any deep-learning projects.",
  },
];

/* ---------- Team (real) ---------- */
export const team = [
  { name: "Ahsan Javed", role: "Chief Executive Officer" },
  { name: "Fahad Razzaq", role: "Chief Technical Officer" },
  { name: "Saad Ali", role: "Embedded & Design Engineer" },
  { name: "Fakhar Alam", role: "Technology Lead" },
  { name: "Ammar Javid", role: "Chief Marketing Officer" },
  { name: "Arzoo Niaz", role: "Full Stack Developer" },
];

const siteContent = {
  brand,
  corePromise,
  contact,
  navLinks,
  whatWeDo,
  problems,
  work,
  products,
  solutionPillars,
  enterpriseSystems,
  integrationAreas,
  storyScenes,
  journey,
  whyChoose,
  stats,
  digitalProducts,
  industrialPartner,
  testimonials,
  team,
};

export default siteContent;
